/**
 * 1.99.5 · Servidor local: versiones compatibles.
 *
 * Antes el puesto y el servidor tenían que tener la misma versión exacta: mientras se actualizaba la sucursal,
 * los puestos quedaban parados. Ahora se miran dos cosas:
 *  · el protocolo (LAN_PROTOCOL): cómo se hablan. Se sube cuando cambia el saludo o un pedido de forma que el
 *    otro lado viejo no lo entendería; LAN_PROTOCOL_MIN es el más viejo con el que todavía se puede hablar.
 *  · la base: los puestos usan la base del servidor, así que las tablas y columnas tienen que ser las mismas
 *    (una huella del esquema).
 * Si las dos coinciden, trabajan juntos aunque la versión de la app sea otra (el que tiene la vieja se actualiza
 * igual, sin cortar). Un equipo que no manda el protocolo es de antes de la 1.99.5: misma versión exacta.
 *
 * Al agregar un pedido nuevo al servidor: si el puesto no puede funcionar sin él, subir LAN_PROTOCOL y
 * LAN_PROTOCOL_MIN; si tiene un camino para servidores viejos (como `sub` → `q`), alcanza con LAN_PROTOCOL.
 */

export const LAN_PROTOCOL = 2;
export const LAN_PROTOCOL_MIN = 2;

export interface PeerCompat {
  /** Versión de la app. */
  v: string;
  /** Protocolo (0 = equipo de antes de la 1.99.5). */
  pv: number;
  /** El protocolo más viejo con el que habla. */
  pmin: number;
  /** Huella del esquema de la base. */
  sh: string;
}

/** ¿Pueden trabajar juntos? */
export function compatible(mine: PeerCompat, peer: PeerCompat): boolean {
  // Un equipo de antes de la 1.99.5 (sin protocolo): la misma versión exacta (en la práctica no entra: también le
  // falta la clave ECDH de la conexión; se deja por si el saludo llega incompleto).
  if (!peer.pv || !mine.pv) return peer.v === mine.v;
  return peer.pv >= mine.pmin && mine.pv >= peer.pmin && !!peer.sh && peer.sh === mine.sh;
}

/** Lo que dijo cada lado en el saludo, en un orden fijo (entra en la clave de la sesión: no se puede cambiar). */
export function handshakeTranscript(client: PeerCompat, server: PeerCompat & { id: string }, biz: string, dev: string): string {
  return JSON.stringify([client.v, client.pv, client.pmin, client.sh, biz, dev, server.id, server.v, server.pv, server.pmin, server.sh]);
}

/** Lo que dice el saludo del otro lado (lo que falta o no se entiende cuenta como equipo viejo). */
export function peerCompat(m: Record<string, unknown>): PeerCompat {
  const int = (x: unknown) => (typeof x === "number" && Number.isInteger(x) && x > 0 ? x : 0);
  return { v: String(m.v ?? ""), pv: int(m.pv), pmin: int(m.pmin), sh: typeof m.sh === "string" ? m.sh.slice(0, 64) : "" };
}

/** FNV-1a de 32 bits, dos veces con otra semilla: corto y estable (no es para seguridad). */
function fnv(s: string, seed: number): string {
  let h = seed >>> 0;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; }
  return h.toString(16).padStart(8, "0");
}

/**
 * Huella del esquema: tablas y columnas con su tipo (sin los índices: no cambian lo que se puede leer).
 * Recibe lo que da `Schema.toJSON()` de PowerSync. Si no tiene la forma esperada devuelve null (y entonces se
 * pide la misma versión exacta).
 */
/**
 * 1.99.19: tablas solo del servidor (los puestos no las usan): no cuentan para la compatibilidad, así un puesto con la
 * versión anterior sigue trabajando mientras se actualiza.
 */
const SERVER_ONLY = new Set(["local_tx_keys"]);

export function schemaSignature(json: unknown, extra: string[] = []): string | null {
  const tables = (json as { tables?: unknown } | null)?.tables;
  if (!Array.isArray(tables) || !tables.length) return null;
  const parts: string[] = [];
  for (const t of tables as { name?: unknown; columns?: unknown; local_only?: unknown; insert_only?: unknown; view_name?: unknown }[]) {
    if (typeof t?.name !== "string" || !Array.isArray(t.columns)) return null;
    if (SERVER_ONLY.has(t.name)) continue;
    const cols = (t.columns as { name?: unknown; type?: unknown }[]).map((c) => `${String(c?.name)}:${String(c?.type)}`).sort();
    const view = typeof t.view_name === "string" && t.view_name !== t.name ? `=${t.view_name}` : "";
    parts.push(`${t.name}${view}${t.local_only ? "!" : ""}${t.insert_only ? "+" : ""}(${cols.join(",")})`);
  }
  // `extra`: lo que decide qué sube el servidor a la nube (las tablas que se pueden escribir y cómo): si un puesto
  // nuevo escribe una tabla que el servidor viejo no sube, se perdería.
  const text = `${parts.sort().join(";")}#${[...extra].sort().join(";")}`;
  return `${fnv(text, 0x811c9dc5)}${fnv(text, 0x050c5d1f)}`;
}
